import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    GitBranch,
    Shield,
    ShieldAlert,
    ShieldCheck,
    AlertTriangle,
    CheckCircle2,
    Clock,
    User,
    Mail,
    KeyRound,
    Terminal,
    FileSearch,
    Share2,
    RefreshCw,
    ExternalLink,
    Crosshair,
    Check,
    ChevronRight,
    Play,
    Zap,
    Lock
} from 'lucide-react';
import { backendURL } from '../config';
import AbuseIPDBPopover from '../components/AbuseIPDBPopover';

export default function AttackStoryExplorer() {
    const [loading, setLoading] = useState(true);
    const [stories, setStories] = useState([]);
    const [selectedStoryId, setSelectedStoryId] = useState(null);
    const [selectedStep, setSelectedStep] = useState(null);
    const [actionStatus, setActionStatus] = useState({});

    const fetchStories = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/attack-stories`);
            if (res.data && res.data.success && res.data.stories?.length > 0) {
                setStories(res.data.stories);
                setSelectedStoryId(res.data.stories[0].id);
                setSelectedStep(res.data.stories[0].stages[0]);
            }
        } catch (err) {
            console.error("Failed to fetch attack stories:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStories();
    }, []);

    const currentStory = stories.find(s => s.id === selectedStoryId) || stories[0];

    const handleSelectStory = (story) => {
        setSelectedStoryId(story.id);
        setSelectedStep(story.stages[0]);
    };

    const handleTriggerContainment = (actionIndex) => {
        setActionStatus(prev => ({ ...prev, [`${selectedStoryId}-${actionIndex}`]: "executing" }));
        setTimeout(() => {
            setActionStatus(prev => ({ ...prev, [`${selectedStoryId}-${actionIndex}`]: "completed" }));
        }, 1200);
    };

    const getStageIcon = (iconName) => {
        switch (iconName) {
            case 'Mail': return Mail;
            case 'KeyRound': return KeyRound;
            case 'FileSearch': return FileSearch;
            case 'Share2': return Share2;
            case 'Terminal': return Terminal;
            case 'ShieldAlert': return ShieldAlert;
            default: return Shield;
        }
    };

    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-purple-600/10 border border-purple-500/20 text-purple-400">
                        <GitBranch className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                Multi-Vendor Attack Story &amp; MITRE Correlation
                            </h1>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30 font-mono">
                                CYBER KILL-CHAIN
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono">
                                5 ENGINES CORRELATED
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                            Unified Incident Narrative: Correlating ThreatGate MXDR, Entra ID, Microsoft Purview, Defender / ThreatDown EDR &amp; FortiGate Firewalls
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={fetchStories}
                        disabled={loading}
                        className="px-3 py-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] rounded-xl text-xs font-semibold text-slate-200 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-purple-400 ${loading ? 'animate-spin' : ''}`} />
                        <span>Refresh Kill-Chains</span>
                    </button>
                </div>
            </div>

            {/* Story Selector Bar */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {stories.map(story => {
                    const isSelected = story.id === selectedStoryId;
                    return (
                        <div
                            key={story.id}
                            onClick={() => handleSelectStory(story)}
                            className={`p-4 rounded-xl border transition-all cursor-pointer ${
                                isSelected
                                    ? 'bg-[#11192e] border-purple-500/50 shadow-lg shadow-purple-500/10'
                                    : 'bg-[#0d1424] border-white/[0.06] hover:border-white/[0.15]'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-mono font-bold text-purple-400">{story.id}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase font-mono ${
                                    story.severity === 'Critical' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                }`}>
                                    {story.severity}
                                </span>
                            </div>
                            <h2 className="text-sm font-bold text-slate-100 mt-1.5 line-clamp-1">{story.title}</h2>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{story.summary}</p>
                            <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-white/[0.04] text-[11px] font-mono text-slate-400">
                                <span>Target: <strong className="text-slate-200">{story.targetEntity}</strong></span>
                                <span>•</span>
                                <span>{story.stages.length} Stages</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Active Story Workspace */}
            {currentStory && (
                <div className="space-y-6">
                    {/* Story Overview Banner */}
                    <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 font-mono text-xs">
                                <span className="text-purple-400 font-bold">{currentStory.id}</span>
                                <span className="text-slate-500">•</span>
                                <span className="text-slate-300">{currentStory.status}</span>
                                <span className="text-slate-500">•</span>
                                <span className="text-slate-400">{new Date(currentStory.timestamp).toLocaleString()}</span>
                            </div>
                            <h2 className="text-base font-bold text-slate-100">{currentStory.title}</h2>
                            <p className="text-xs text-slate-300 leading-relaxed max-w-4xl">{currentStory.summary}</p>
                        </div>
                        <div className="flex flex-col items-start md:items-end gap-1 font-mono text-xs shrink-0">
                            <span className="text-slate-400">Target Asset:</span>
                            <span className="font-bold text-slate-100">{currentStory.affectedAsset}</span>
                        </div>
                    </div>

                    {/* Visual Multi-Vendor Kill-Chain Pipeline */}
                    <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-4">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono flex items-center gap-2">
                                <Crosshair className="w-3.5 h-3.5 text-purple-400" />
                                <span>Correlated Cyber Kill-Chain Stages ({currentStory.stages.length} Sequential Steps)</span>
                            </span>
                            <span className="text-[10px] font-mono text-slate-400">Click any stage to view forensic telemetry</span>
                        </div>

                        {/* Pipeline Stage Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 relative">
                            {currentStory.stages.map((stage, idx) => {
                                const isSelected = selectedStep?.step === stage.step;
                                const StageIcon = getStageIcon(stage.icon);
                                return (
                                    <div
                                        key={stage.step}
                                        onClick={() => setSelectedStep(stage)}
                                        className={`p-3.5 rounded-xl border transition-all cursor-pointer relative group flex flex-col justify-between ${
                                            isSelected
                                                ? 'bg-[#11192e] border-purple-500 shadow-md shadow-purple-500/10'
                                                : 'bg-[#070b14] border-white/[0.06] hover:border-white/[0.15]'
                                        }`}
                                    >
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="w-5 h-5 rounded-full bg-white/[0.06] text-slate-300 font-mono text-[10px] font-bold flex items-center justify-center">
                                                    {stage.step}
                                                </span>
                                                <span
                                                    className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase"
                                                    style={{ backgroundColor: `${stage.vendorColor}20`, color: stage.vendorColor, border: `1px solid ${stage.vendorColor}40` }}
                                                >
                                                    {stage.vendorBadge}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <div className="p-1.5 rounded-lg bg-white/[0.04] text-slate-300 group-hover:text-purple-400 transition-colors">
                                                    <StageIcon className="w-4 h-4" />
                                                </div>
                                                <span className="text-[11px] font-bold text-slate-200 line-clamp-1">{stage.stageName}</span>
                                            </div>

                                            <p className="text-[11px] text-slate-400 line-clamp-2 font-medium">
                                                {stage.action}
                                            </p>
                                        </div>

                                        <div className="mt-3 pt-2 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono text-slate-400">
                                            <span className="text-purple-300 font-bold">{stage.mitreId}</span>
                                            <span className={`px-1.5 py-0.2 rounded ${
                                                stage.status === 'Blocked' ? 'text-emerald-400 bg-emerald-500/10' :
                                                stage.status === 'Intercepted' ? 'text-blue-400 bg-blue-500/10' :
                                                stage.status === 'Flagged' ? 'text-orange-400 bg-orange-500/10' : 'text-slate-300'
                                            }`}>
                                                {stage.status}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Selected Stage Forensic Deep-Dive */}
                    {selectedStep && (
                        <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-4 animate-in fade-in duration-200">
                            <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                                <div className="flex items-center gap-2.5">
                                    <span className="px-2 py-0.5 rounded text-xs font-mono font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                        STAGE {selectedStep.step}: {selectedStep.stageName.toUpperCase()}
                                    </span>
                                    <span className="text-xs font-mono text-slate-400">
                                        Detection Engine: <strong className="text-slate-200">{selectedStep.vendor}</strong>
                                    </span>
                                </div>
                                <span className="text-xs font-mono text-slate-400">
                                    {new Date(selectedStep.timestamp).toLocaleString()}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-mono">
                                <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">Observed Security Event</span>
                                    <span className="font-bold text-slate-100">{selectedStep.action}</span>
                                </div>
                                <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">MITRE ATT&amp;CK Technique</span>
                                    <span className="font-bold text-purple-300">{selectedStep.mitreId} - {selectedStep.mitreName}</span>
                                </div>
                                <div className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] space-y-1">
                                    <span className="text-[10px] uppercase text-slate-500 block">Enforcement Status</span>
                                    <span className="font-bold text-emerald-400">{selectedStep.status}</span>
                                </div>
                            </div>

                            {/* Narrative */}
                            <div className="p-4 rounded-xl bg-purple-950/20 border border-purple-500/20 space-y-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400 font-mono">
                                    Cross-Vendor Forensic Context
                                </span>
                                <p className="text-xs text-slate-200 leading-relaxed font-sans">
                                    {selectedStep.narrative}
                                </p>
                            </div>

                            {/* Raw Evidence Payload */}
                            <div className="space-y-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-mono">
                                    Correlated Telemetry Payload ({selectedStep.vendor})
                                </span>
                                <pre className="p-3.5 rounded-xl bg-[#040711] border border-white/[0.06] text-[11px] font-mono text-slate-300 overflow-x-auto">
                                    {JSON.stringify(selectedStep.rawEvidence, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}

                    {/* Recommended Multi-Vendor Containment Actions */}
                    <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-3.5">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                                <span>Recommended Multi-Vendor SOAR Containment Playbooks</span>
                            </span>
                            <span className="text-[10px] font-mono text-slate-400">Strict Read-Only Verification Mode Active</span>
                        </div>

                        <div className="space-y-2.5">
                            {currentStory.recommendedContainment.map((action, idx) => {
                                const actionKey = `${selectedStoryId}-${idx}`;
                                const state = actionStatus[actionKey];
                                return (
                                    <div
                                        key={idx}
                                        className="p-3.5 rounded-xl bg-black/40 border border-white/[0.04] flex items-center justify-between gap-4 text-xs"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center shrink-0 font-mono font-bold text-[10px]">
                                                {idx + 1}
                                            </div>
                                            <span className="text-slate-200 font-medium">{action}</span>
                                        </div>

                                        <button
                                            onClick={() => handleTriggerContainment(idx)}
                                            disabled={state === 'completed' || state === 'executing'}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                                                state === 'completed'
                                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                    : state === 'executing'
                                                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 animate-pulse'
                                                    : 'bg-white/[0.06] hover:bg-white/[0.1] text-slate-200 border border-white/[0.08]'
                                            }`}
                                        >
                                            {state === 'completed' ? (
                                                <>
                                                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                                                    <span>Verified</span>
                                                </>
                                            ) : state === 'executing' ? (
                                                <span>Verifying...</span>
                                            ) : (
                                                <>
                                                    <Play className="w-3.5 h-3.5 text-purple-400" />
                                                    <span>Validate Action</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {!loading && stories.length === 0 && (
                <div className="p-12 text-center rounded-2xl bg-[#0d1424] border border-white/[0.06] space-y-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto">
                        <ShieldCheck className="w-6 h-6" />
                    </div>
                    <h3 className="text-base font-bold text-slate-100">Zero Active Multi-Stage Kill-Chains</h3>
                    <p className="text-xs text-slate-400 max-w-md mx-auto">
                        No critical intrusions are currently traversing the cyber kill-chain. Perimeter firewalls, ThreatDown EDR, and Defender XDR report clean operating conditions.
                    </p>
                </div>
            )}
        </div>
    );
}
